import React, { useEffect, useRef, useState, useCallback } from "react";
import { HiFilter, HiSearch, HiX } from "react-icons/hi";
import axios from "axios";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";

const getAvatarUrl = (avatar, name = "User") => {
  if (
    typeof avatar === "string" &&
    avatar.trim().length > 0 &&
    !avatar.includes("via.placeholder.com")
  ) {
    return avatar;
  }
  if (avatar && typeof avatar === "object" && avatar.avatarUrl) {
    return avatar.avatarUrl;
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(
    name || "User"
  )}&background=c51369&color=fff`;
};

const highlightMatch = (text, query) => {
  if (!text) return "—";
  if (!query || !query.trim()) return text;

  try {
    const escaped = query.trim().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    const parts = String(text).split(regex);

    if (parts.length <= 1) return text;

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark
              key={i}
              className="rounded bg-pink-100 px-1 py-0.5 font-semibold text-[#c51369]"
            >
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  } catch (e) {
    return text;
  }
};

const truncateMessage = (msg, maxLen = 50, query = "") => {
  if (!msg) return "—";
  if (msg.length <= maxLen) return msg;

  if (query && query.trim()) {
    const q = query.trim().toLowerCase();
    const matchIndex = msg.toLowerCase().indexOf(q);
    if (matchIndex > 20) {
      const start = Math.max(0, matchIndex - 15);
      const end = Math.min(msg.length, matchIndex + q.length + 30);
      return `${start > 0 ? "..." : ""}${msg.substring(start, end)}${
        end < msg.length ? "..." : ""
      }`;
    }
  }

  return msg.substring(0, maxLen) + "...";
};

const MessageHistoryTable = () => {
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("All Types");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const typeDropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const toggleTypeDropdown = () => setIsTypeDropdownOpen(!isTypeDropdownOpen);

  // Centralized, optimized API fetcher with cancellation
  const getMessages = useCallback(
    async (page = 1, currentFilters = filters) => {
      // Abort previous in-flight request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const params = new URLSearchParams({ page, limit: 10 });

        if (currentFilters?.startDate) {
          params.append("startDate", currentFilters.startDate);
        }
        if (currentFilters?.endDate) {
          params.append("endDate", currentFilters.endDate);
        }
        if (currentFilters?.messageType) {
          params.append("messageType", currentFilters.messageType);
        }
        if (currentFilters?.search && currentFilters.search.trim()) {
          params.append("search", currentFilters.search.trim());
        }

        setLoading(true);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/chat/get-all-messages?${params.toString()}`,
          { signal: controller.signal }
        );

        setMessages(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setCurrentPage(response.data.pagination?.currentPage || 1);
        setTotalCount(response.data.pagination?.totalCount || 0);
        setLoading(false);
        setIsSearching(false);
      } catch (error) {
        if (axios.isCancel(error) || error.name === "CanceledError") {
          // Quietly ignore superseded/cancelled request
          return;
        }
        console.error("Error fetching messages:", error);
        setLoading(false);
        setIsSearching(false);
      }
    },
    [filters]
  );

  // Initial load
  useEffect(() => {
    getMessages(1, {});
  }, []);

  // Cleanup pending timeouts and requests on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Close type dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target)
      ) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle Type Filter Click
  const handleTypeClick = (typeOption, typeValue) => {
    setSelectedType(typeOption);
    const newFilters = { ...filters, messageType: typeValue };
    setFilters(newFilters);
    setCurrentPage(1);
    getMessages(1, newFilters);
    setIsTypeDropdownOpen(false);
  };

  // Handle Date Filter Change
  const handleDateChange = (type, value) => {
    const newFilters = { ...filters };

    if (type === "startDate") {
      setStartDate(value);
      if (value) {
        const start = new Date(value);
        start.setHours(0, 0, 0, 0);
        newFilters.startDate = start.toISOString();
      } else {
        delete newFilters.startDate;
      }
    } else if (type === "endDate") {
      setEndDate(value);
      if (value) {
        const end = new Date(value);
        end.setHours(23, 59, 59, 999);
        newFilters.endDate = end.toISOString();
      } else {
        delete newFilters.endDate;
      }
    }

    setFilters(newFilters);
    setCurrentPage(1);
    getMessages(1, newFilters);
  };

  // Debounced search (400ms) with proper cancellation
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setIsSearching(true);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      const newFilters = { ...filters };
      if (value.trim()) {
        newFilters.search = value.trim();
      } else {
        delete newFilters.search;
      }
      setFilters(newFilters);
      setCurrentPage(1);
      getMessages(1, newFilters);
    }, 400);
  };

  // Instant clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const newFilters = { ...filters };
    delete newFilters.search;
    setFilters(newFilters);
    setCurrentPage(1);
    getMessages(1, newFilters);
  };

  // Pagination Change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    getMessages(page, filters);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    return `${date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })} ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  return (
    <div className="py-5">
      {/* Title */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          Message History
          {totalCount > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-400">
              ({totalCount.toLocaleString()} total)
            </span>
          )}
        </h2>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col items-center justify-start gap-2 space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
        {/* Search with Debounce and Clear Button */}
        <div className="md:min-w-1/3 relative w-full md:w-1/3">
          <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by message words, user, or phone..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="block w-full rounded-md border py-2 pl-10 pr-9 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              <HiX className="h-4 w-4" />
            </button>
          ) : isSearching ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-pink-300 border-t-[#c51369]"></span>
            </span>
          ) : null}
        </div>

        {/* Message Type Dropdown */}
        <div
          className="md:min-w-1/5 relative w-full md:w-1/5"
          ref={typeDropdownRef}
        >
          <button
            className="flex w-full items-center rounded-md border bg-white px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
            onClick={toggleTypeDropdown}
          >
            <HiFilter color="#c51369" className="mr-2 h-5 w-5" />
            {selectedType}
          </button>
          {isTypeDropdownOpen && (
            <div className="absolute left-0 z-10 mt-2 w-full rounded-md border bg-white shadow-lg">
              <ul className="py-2">
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleTypeClick("All Types", null)}
                >
                  All Types
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleTypeClick("Text", "text")}
                >
                  Text
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleTypeClick("Image", "image")}
                >
                  Image
                </li>
                <li
                  className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                  onClick={() => handleTypeClick("Video", "video")}
                >
                  Video
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Start Date */}
        <div className="md:min-w-1/5 w-full md:w-1/5">
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange("startDate", e.target.value)}
            className="block w-full rounded-md border px-4 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
          />
        </div>

        {/* End Date */}
        <div className="md:min-w-1/5 w-full md:w-1/5">
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange("endDate", e.target.value)}
            className="block w-full rounded-md border px-4 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#c51369]"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <TableLoader />
      ) : (
        <div className="mx-auto mt-5 w-auto rounded-t-2xl">
          <div className="overflow-x-auto rounded-t-2xl">
            <table className="w-full rounded-t-2xl text-left text-sm">
              <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
                <tr>
                  <th className="px-6 py-3">Sl No.</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Sender</th>
                  <th className="px-6 py-3">Receiver</th>
                  <th className="px-6 py-3">Message</th>
                  <th className="px-6 py-3">Date & Time</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {messages.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-10 text-center text-gray-500"
                    >
                      {filters?.search
                        ? `No messages found matching "${filters.search}"`
                        : "No messages found"}
                    </td>
                  </tr>
                ) : (
                  messages.map((msg, index) => (
                    <tr
                      key={msg._id || index}
                      className="text-md cursor-pointer bg-white tracking-wide text-gray-700 transition-colors hover:bg-pink-50"
                      onClick={() =>
                        setSelectedMessage(
                          selectedMessage?._id === msg._id ? null : msg
                        )
                      }
                    >
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            msg.messageType === "text"
                              ? "bg-blue-100 text-blue-700"
                              : msg.messageType === "image"
                              ? "bg-green-100 text-green-700"
                              : msg.messageType === "video"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {msg.messageType || "text"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        <div className="flex items-center">
                          <img
                            src={getAvatarUrl(
                              msg?.senderUser?.selected_Avatar,
                              msg?.senderUser?.nickName ||
                                msg?.senderUser?.fullName
                            )}
                            alt="avatar"
                            className="h-10 w-10 rounded-full object-cover"
                          />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-800">
                              {highlightMatch(
                                msg?.senderUser?.nickName ||
                                  msg?.senderUser?.fullName ||
                                  "Unknown",
                                filters?.search || searchQuery
                              )}
                            </p>
                            <p className="text-xs text-gray-400">
                              {msg?.senderUser?.phone
                                ? highlightMatch(
                                    `+${msg.senderUser.phone}`,
                                    filters?.search || searchQuery
                                  )
                                : "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        <div className="flex items-center">
                          <img
                            src={getAvatarUrl(
                              msg?.receiverUser?.selected_Avatar,
                              msg?.receiverUser?.nickName ||
                                msg?.receiverUser?.fullName
                            )}
                            alt="avatar"
                            className="h-10 w-10 rounded-full object-cover"
                          />
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-800">
                              {highlightMatch(
                                msg?.receiverUser?.nickName ||
                                  msg?.receiverUser?.fullName ||
                                  "Unknown",
                                filters?.search || searchQuery
                              )}
                            </p>
                            <p className="text-xs text-gray-400">
                              {msg?.receiverUser?.phone
                                ? highlightMatch(
                                    `+${msg.receiverUser.phone}`,
                                    filters?.search || searchQuery
                                  )
                                : "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td
                        className="border border-[#ddd9db] px-6 py-4"
                        style={{ maxWidth: "280px" }}
                      >
                        <p className="truncate text-sm text-gray-700">
                          {msg.messageType === "image"
                            ? "📷 Image"
                            : msg.messageType === "video"
                            ? "🎥 Video"
                            : highlightMatch(
                                truncateMessage(
                                  msg.message,
                                  55,
                                  filters?.search || searchQuery
                                ),
                                filters?.search || searchQuery
                              )}
                        </p>
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4 text-sm">
                        {formatDateTime(msg.timestamp)}
                      </td>
                      <td className="whitespace-nowrap border border-[#ddd9db] px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                            msg.seen
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {msg.seen ? "Seen" : "Unseen"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Expanded Message Detail */}
          {selectedMessage && (
            <div className="mt-3 rounded-xl border border-pink-100 bg-pink-50/50 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">
                  Message Details
                </h4>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  ✕ Close
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-lg bg-white p-3">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Sender
                  </p>
                  <div className="flex items-center gap-2">
                    <img
                      src={getAvatarUrl(
                        selectedMessage?.senderUser?.selected_Avatar,
                        selectedMessage?.senderUser?.nickName
                      )}
                      alt="sender"
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {highlightMatch(
                          selectedMessage?.senderUser?.nickName ||
                            selectedMessage?.senderUser?.fullName ||
                            "Unknown",
                          filters?.search || searchQuery
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        {selectedMessage?.senderUser?.phone
                          ? highlightMatch(
                              `+${selectedMessage.senderUser.phone}`,
                              filters?.search || searchQuery
                            )
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg bg-white p-3">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Receiver
                  </p>
                  <div className="flex items-center gap-2">
                    <img
                      src={getAvatarUrl(
                        selectedMessage?.receiverUser?.selected_Avatar,
                        selectedMessage?.receiverUser?.nickName
                      )}
                      alt="receiver"
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {highlightMatch(
                          selectedMessage?.receiverUser?.nickName ||
                            selectedMessage?.receiverUser?.fullName ||
                            "Unknown",
                          filters?.search || searchQuery
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        {selectedMessage?.receiverUser?.phone
                          ? highlightMatch(
                              `+${selectedMessage.receiverUser.phone}`,
                              filters?.search || searchQuery
                            )
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 rounded-lg bg-white p-3">
                <p className="mb-1 text-xs font-medium text-gray-500">
                  Full Message
                </p>
                {selectedMessage.messageType === "image" ? (
                  <img
                    src={selectedMessage.message}
                    alt="shared"
                    className="max-h-60 rounded-lg object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : selectedMessage.messageType === "video" ? (
                  <p className="text-sm text-gray-600">🎥 Video message</p>
                ) : (
                  <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                    {highlightMatch(
                      selectedMessage.message || "—",
                      filters?.search || searchQuery
                    )}
                  </p>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="text-sm font-medium capitalize">
                    {selectedMessage.messageType || "text"}
                  </p>
                </div>
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">Date & Time</p>
                  <p className="text-sm font-medium">
                    {formatDateTime(selectedMessage.timestamp)}
                  </p>
                </div>
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">Status</p>
                  <p
                    className={`text-sm font-medium ${
                      selectedMessage.seen
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {selectedMessage.seen ? "Seen" : "Unseen"}
                    {selectedMessage.seenAt &&
                      ` at ${formatDateTime(selectedMessage.seenAt)}`}
                  </p>
                </div>
                <div className="rounded-lg bg-white p-3">
                  <p className="text-xs text-gray-500">Message ID</p>
                  <p className="truncate font-mono text-xs text-gray-500">
                    {selectedMessage._id}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pagination */}
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default MessageHistoryTable;
